import { useState, useRef } from 'react';
import AdminLayout from '../../components/admin/AdminLayout';
import { POSITIONS, TT_REGIONS } from '../../utils/helpers';
import { useTenantCollection, useTenantCrud } from '../../hooks/useTenantData';
import { useClub } from '../../contexts/ClubContext';
import PlayerDocuments from '../../components/admin/PlayerDocuments';
import { uploadImage, deleteFile, generateFilename, getStoragePath, isStorageUrl } from '../../services/storageService';

function ManagePlayers() {
  // Use tenant-scoped data hooks
  const { data: players, loading, setData: setPlayers } = useTenantCollection('players');
  const { add, update, remove } = useTenantCrud('players');
  const { clubId } = useClub();

  const [showModal, setShowModal] = useState(false);
  const [showDocsModal, setShowDocsModal] = useState(false);
  const [selectedPlayer, setSelectedPlayer] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [editingPlayer, setEditingPlayer] = useState(null);
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState('');
  
  const emptyPlayer = {
    name: '',
    position: '',
    number: '',
    team: '',
    age: '',
    goals: 0,
    assists: 0,
    region: '',
    image: ''
  };
  
  const [formData, setFormData] = useState(emptyPlayer);
  const fileInputRef = useRef(null);

  const teams = ['U8', 'U10', 'U12', 'U14', 'U16', 'U18', 'U8 Tier 2', 'U10 Tier 2', 'U12 Tier 2', 'U14 Tier 1', 'U14 Tier 2', 'U16 Tier 1', 'U16 Tier 2', 'U18 Tier 1', 'U18 Tier 2'];

  const openAddModal = () => {
    setEditingPlayer(null);
    setFormData(emptyPlayer);
    setImageFile(null);
    setImagePreview('');
    setError('');
    setShowModal(true);
  };

  const handleImageSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      setError('Please select an image file');
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      setError('Image must be less than 5MB');
      return;
    }

    // Store file for upload on save, show preview
    setImageFile(file);
    const previewUrl = URL.createObjectURL(file);
    setImagePreview(previewUrl);
    setError('');
  };

  const openEditModal = (player) => {
    setEditingPlayer(player);
    setFormData({
      name: player.name || '',
      position: player.position || '',
      number: player.number || '',
      team: player.team || '',
      age: player.age || '',
      goals: player.goals || 0,
      assists: player.assists || 0,
      region: player.region || '',
      image: player.image || ''
    });
    setImageFile(null);
    setImagePreview(player.image || '');
    setError('');
    setShowModal(true);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setError('');
    setSaving(true);

    try {
      let imageUrl = formData.image;
      
      // Upload new image if selected
      if (imageFile) {
        const filename = generateFilename(imageFile.name);
        const playerId = editingPlayer?.id || `new-${Date.now()}`;
        const path = `${getStoragePath.playerImage(clubId, playerId)}/${filename}`;
        imageUrl = await uploadImage(imageFile, path);
        
        // Delete old image if it was in storage
        if (editingPlayer?.image && isStorageUrl(editingPlayer.image)) {
          try {
            await deleteFile(editingPlayer.image);
          } catch (err) {
            console.warn('Could not delete old image:', err);
          }
        }
      }

      const playerData = {
        ...formData,
        image: imageUrl,
        number: parseInt(formData.number) || 0,
        age: parseInt(formData.age) || 0,
        goals: parseInt(formData.goals) || 0,
        assists: parseInt(formData.assists) || 0
      };

      if (editingPlayer) {
        // Update existing player (tenant-scoped)
        const updated = await update(editingPlayer.id, playerData);
        setPlayers(players.map(p => 
          p.id === editingPlayer.id ? updated : p
        ));
      } else {
        // Add new player (tenant-scoped)
        const newPlayer = await add(playerData);
        setPlayers([...players, newPlayer]);
      }

      setShowModal(false);
      setFormData(emptyPlayer);
      setImageFile(null);
      setImagePreview('');
    } catch (error) {
      console.error('Error saving player:', error);
      setError('Failed to save player. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (playerId, playerName) => {
    if (!window.confirm(`Are you sure you want to delete ${playerName}?`)) return;
    
    try {
      // Delete player (tenant-scoped)
      await remove(playerId);
      setPlayers(players.filter(p => p.id !== playerId));
    } catch (error) {
      console.error('Error deleting player:', error);
    }
  };

  return (
    <AdminLayout title="Manage Players" subtitle="Add and manage spotlight players for the homepage carousel">
      <div className="admin-page-header">
        <div></div>
        <button className="btn-add" onClick={openAddModal}>
          + Add Player
        </button>
      </div>

      {/* Players Table */}
      <div className="admin-card">
        <div className="card-content">
          {loading ? (
            <p className="loading-text">Loading players...</p>
          ) : players.length === 0 ? (
            <div className="empty-state">
              <span className="empty-icon">⚽</span>
              <h3>No Players Yet</h3>
              <p>Add spotlight players to feature them on the homepage.</p>
              <button className="btn-add" onClick={openAddModal}>
                + Add Player
              </button>
            </div>
          ) : (
            <table className="admin-table">
              <thead>
                <tr>
                  <th>Player</th>
                  <th>Position</th>
                  <th>Team</th>
                  <th>Stats</th>
                  <th>Region</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {players.map(player => (
                  <tr key={player.id}>
                    <td>
                      <div className="player-cell">
                        {player.image && (
                          <img src={player.image} alt={player.name} className="player-thumb" />
                        )}
                        <div>
                          <strong>{player.name}</strong>
                          <span className="player-number">#{player.number}</span>
                        </div>
                      </div>
                    </td>
                    <td>{player.position}</td>
                    <td><span className="badge">{player.team}</span></td>
                    <td>
                      <span className="stat-mini">{player.goals} G</span>
                      <span className="stat-mini">{player.assists} A</span>
                    </td>
                    <td><span className="region-badge">{player.region || '-'}</span></td>
                    <td>
                      <div className="action-buttons">
                        <button 
                          className="btn-docs" 
                          onClick={() => {
                            setSelectedPlayer(player);
                            setShowDocsModal(true);
                          }}
                          title="Manage Documents"
                        >
                          📄 Docs
                        </button>
                        <button className="btn-edit" onClick={() => openEditModal(player)}>
                          Edit
                        </button>
                        <button className="btn-delete" onClick={() => handleDelete(player.id, player.name)}>
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {/* Add/Edit Player Modal */}
      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal-content modal-large" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{editingPlayer ? 'Edit Player' : 'Add New Player'}</h2>
              <button className="modal-close" onClick={() => setShowModal(false)}>×</button>
            </div>
            <form onSubmit={handleSave}>
              <div className="modal-body">
                {error && <div className="form-error">{error}</div>}
                
                <div className="form-row">
                  <div className="form-group">
                    <label>Player Name *</label>
                    <input
                      type="text"
                      value={formData.name}
                      onChange={e => setFormData({...formData, name: e.target.value})}
                      placeholder="Full name"
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label>Jersey Number *</label>
                    <input
                      type="number"
                      value={formData.number}
                      onChange={e => setFormData({...formData, number: e.target.value})}
                      placeholder="10"
                      min="1"
                      max="99"
                      required
                    />
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label>Position *</label>
                    <select
                      value={formData.position}
                      onChange={e => setFormData({...formData, position: e.target.value})}
                      required
                    >
                      <option value="">Select position...</option>
                      {POSITIONS.map(pos => (
                        <option key={pos} value={pos}>{pos}</option>
                      ))}
                    </select>
                  </div>
                  <div className="form-group">
                    <label>Team *</label>
                    <select
                      value={formData.team}
                      onChange={e => setFormData({...formData, team: e.target.value})}
                      required
                    >
                      <option value="">Select team...</option>
                      {teams.map(team => (
                        <option key={team} value={team}>{team}</option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label>Age</label>
                    <input
                      type="number"
                      value={formData.age}
                      onChange={e => setFormData({...formData, age: e.target.value})}
                      placeholder="15"
                      min="5"
                      max="25"
                    />
                  </div>
                  <div className="form-group">
                    <label>Region</label>
                    <select
                      value={formData.region}
                      onChange={e => setFormData({...formData, region: e.target.value})}
                    >
                      <option value="">Select region...</option>
                      {TT_REGIONS.map(r => (
                        <option key={r} value={r}>{r}</option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label>Goals</label>
                    <input
                      type="number"
                      value={formData.goals}
                      onChange={e => setFormData({...formData, goals: e.target.value})}
                      placeholder="0"
                      min="0"
                    />
                  </div>
                  <div className="form-group">
                    <label>Assists</label>
                    <input
                      type="number"
                      value={formData.assists}
                      onChange={e => setFormData({...formData, assists: e.target.value})}
                      placeholder="0"
                      min="0"
                    />
                  </div>
                </div>

                <div className="form-group">
                  <label>Player Photo</label>
                  <input
                    type="file"
                    ref={fileInputRef}
                    onChange={handleImageSelect}
                    accept="image/*"
                    style={{ display: 'none' }}
                  />
                  <div className="file-upload-area" onClick={() => fileInputRef.current?.click()}>
                    {(imagePreview || formData.image) ? (
                      <div className="image-preview-inline">
                        <img src={imagePreview || formData.image} alt="Preview" />
                        <span className="change-photo">Click to change photo</span>
                      </div>
                    ) : (
                      <div className="upload-placeholder">
                        <span className="upload-icon">📷</span>
                        <span>Click to select photo</span>
                        <small>JPG, PNG (max 5MB)</small>
                      </div>
                    )}
                  </div>
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn-cancel" onClick={() => setShowModal(false)}>
                  Cancel
                </button>
                <button type="submit" className="btn-save" disabled={saving}>
                  {saving ? 'Saving...' : (editingPlayer ? 'Update Player' : 'Add Player')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Documents Modal */}
      {showDocsModal && selectedPlayer && (
        <div className="modal-overlay" onClick={() => setShowDocsModal(false)}>
          <div className="modal-content modal-xlarge" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Documents - {selectedPlayer.name}</h2>
              <button className="modal-close" onClick={() => setShowDocsModal(false)}>×</button>
            </div>
            <div className="modal-body">
              <PlayerDocuments 
                playerId={selectedPlayer.id} 
                playerName={selectedPlayer.name} 
              />
            </div>
            <div className="modal-footer">
              <button className="btn-cancel" onClick={() => setShowDocsModal(false)}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  );
}

export default ManagePlayers;
